import { useMemo, useState, useEffect, useRef } from 'react';
import { useFilterPersistence } from './useFilterPersistence';
import { useSharedFilters } from './useSharedFilters';

/**
 * Unified hook for page filters that handles both shared and persisted modes
 *
 * This wrapper provides a consistent interface regardless of whether the user
 * is viewing a shared URL or browsing normally with persisted filters.
 *
 * Modes:
 * - Shared mode: Filters from URL, read-only, no persistence
 * - Normal mode: Filters from storage, full read/write, with persistence
 *
 * @param options - Configuration options
 * @returns Filter state and setters with mode indicators
 */
export interface UsePageFiltersOptions<T> {
  storageKey: string;
  defaultFilters: T;
  excludeFields?: string[];
  universalFields?: string[];
  currentBusinessUnitId: string;
}

export interface UsePageFiltersReturn<T> {
  filters: T;
  setFilters: (filters: T | ((prev: T) => T)) => void;
  clearFilters: () => void;
  resetTarget: T; // What to reset to (shared filters in shared mode, defaults in normal mode)
  isSharedMode: boolean;
  isReadOnly: boolean;
  isBUMismatch: boolean;
}

export function usePageFilters<T extends Record<string, any>>(
  options: UsePageFiltersOptions<T>,
): UsePageFiltersReturn<T> {
  const {
    currentBusinessUnitId,
    storageKey,
    defaultFilters,
    excludeFields,
    universalFields,
  } = options;

  // Use ref to track defaultFilters without causing re-renders
  const defaultFiltersRef = useRef(defaultFilters);
  useEffect(() => {
    defaultFiltersRef.current = defaultFilters;
  }, [defaultFilters]);

  // Check for shared filters in URL
  const { sharedFilters, isSharedMode, isBUMismatch } = useSharedFilters<T>({
    currentBusinessUnitId,
  });

  // Always initialize persistence (for normal mode)
  const {
    filters: persistedFilters,
    setFilters: setPersisted,
    clearFilters: clearPersisted,
  } = useFilterPersistence({
    storageKey,
    defaultFilters,
    excludeFields,
    universalFields,
    businessUnitId: currentBusinessUnitId,
  });

  // In shared mode, we need local state to allow filter modifications
  // without persisting them
  const [localSharedFilters, setLocalSharedFilters] = useState<T | null>(null);

  // Sync local state when shared filters change (URL change)
  useEffect(() => {
    if (isSharedMode && sharedFilters) {
      setLocalSharedFilters(sharedFilters);
    }
  }, [isSharedMode, sharedFilters]);

  // Helper to create cleared filters (null for all clearable fields, keeps excluded fields)
  const createClearedFilters = (baseFilters: T): T => {
    const cleared = { ...baseFilters };
    Object.keys(cleared).forEach((key) => {
      if (!excludeFields?.includes(key)) {
        (cleared as any)[key] = null;
      }
    });
    return cleared;
  };

  // Memoize the return value to ensure stable references but update when mode changes
  return useMemo(() => {
    if (isSharedMode && sharedFilters) {
      return {
        filters: localSharedFilters || sharedFilters,
        setFilters: (filters: T | ((prev: T) => T)) => {
          // Allow modifications in shared mode (but don't persist)
          if (typeof filters === 'function') {
            setLocalSharedFilters((prev) => filters(prev || sharedFilters));
          } else {
            setLocalSharedFilters(filters);
          }
        },
        clearFilters: () => {
          // "Clear All" button in shared mode: Clear all filters (no persistence)
          const cleared = createClearedFilters(sharedFilters);
          setLocalSharedFilters(cleared);
        },
        resetTarget: sharedFilters, // Reset to original shared filters
        isSharedMode: true,
        isReadOnly: false, // Allow modifications (just don't persist)
        isBUMismatch,
      };
    }

    return {
      filters: persistedFilters,
      setFilters: setPersisted,
      clearFilters: clearPersisted, // "Clear All" in normal mode: clears and persists
      resetTarget: defaultFiltersRef.current, // Reset to default filters
      isSharedMode: false,
      isReadOnly: false,
      isBUMismatch: false,
    };
  }, [
    isSharedMode,
    sharedFilters,
    localSharedFilters,
    persistedFilters,
    setPersisted,
    clearPersisted,
    isBUMismatch,
    excludeFields,
  ]);
}
